import {
  IAddressResponseData,
  Cart,
  CartPositionType,
  Checkout,
  ICartData,
  IAddressData,
  ICartPosition,
  ICountriesLookup,
  IGetCheckoutShippingOptionsData,
} from "../../../../typescript-library";
import {getShopId} from "../../getShopId";
import {displayName, INTERNAL_ERROR_TYPE, isoLocales, retrieveCheckout, Payments} from "../PaymentController";
import React from "react";
import {splitStreetAndNumber} from "../paypal/helpers/paypalAddressHelpers";
import i18n from "i18next";
import braintree, {googlePayment} from "braintree-web";
import {GPayAuthError} from "./onGoogleAuthorized";
import {BraintreeDataType} from "../../../cart/CartContextProvider/CartContextProvider";
import formatCurrency from "../../../../helpers/formatCurrency";
import {isToNorthIreland} from "../../../checkout/steps/AddressStep/AddressStep";
import {findShippingOption} from "../paypal/helpers/paypalHelperFunctions";

const merchantId = __PRODUCTION__ ? "BCR2DN4TXX43RTJW" : "12345678901234567890";
const googleMerchantId = __PRODUCTION__ ? merchantId : "TEST";

/**
 * Type adapter for `positionToDisplayItem()`
 */
const typeAdapter: Record<CartPositionType, google.payments.api.DisplayItemType> = {
  Item: "LINE_ITEM",
  Shipping: "SHIPPING_OPTION",
  CashDiscount: "DISCOUNT",
  Discount: "DISCOUNT",
  Voucher: "DISCOUNT",
  Point: "DISCOUNT",
  Fee: "TAX",
};

type ExtendedRequestUpdateType = {
  isPhoneRequired?: boolean | undefined;
  requestUpdate: google.payments.api.PaymentDataRequestUpdate;
};

let globalShippingOptions: IGetCheckoutShippingOptionsData[] = [];

/**
 * Adapter method to convert cart positions to gpay `displayItems` via Array.map()
 *
 * @param cart cart holding positions and rebates
 */
export const positionsToDisplayItems = (cart: Cart | ICartData): google.payments.api.DisplayItem[] => [
  ...cart.positions
    .filter((cpos) => !!cpos.text)
    .map(
      (pos: ICartPosition): google.payments.api.DisplayItem => ({
        type: typeAdapter[pos.type],
        label: pos.type === "Item" && pos.quantity > 1 ? `${pos.quantity} x ${pos.text}` : pos.text,
        price: pos.amount.amount,
      }),
    ),
  ...cart.rebates.map(({amount, code}) => ({
    type: "DISCOUNT" as google.payments.api.DisplayItemType,
    label: code,
    price: amount.amount.startsWith("-") ? amount.amount : "-" + amount.amount,
  })),
];

type ReturnType = {
  gPayInstance: braintree.GooglePayment;
  paymentDataRequest: google.payments.api.PaymentDataRequest;
};

export const preparePaymentDataRequest = async (
  braintreeData: BraintreeDataType,
  cart: Cart,
  countries: ICountriesLookup[],
): Promise<ReturnType> => {
  // const clientInstance = await braintree.client.create({authorization: btData.token});
  const gPayInstance = await googlePayment.create({
    client: braintreeData.client,
    googlePayVersion: 2,
    googleMerchantId,
  });

  // This 'await' is a type def divergence. Please keep it in place
  const tempPaymentDataRequest = await gPayInstance.createPaymentDataRequest({
    transactionInfo: {
      currencyCode: cart.currencyCode,
      totalPriceStatus: "ESTIMATED",
      totalPrice: cart.valuePayable.amount,
    },
    emailRequired: true,
  });

  if (!tempPaymentDataRequest.allowedPaymentMethods || !tempPaymentDataRequest.allowedPaymentMethods.length) {
    throw new GPayAuthError(
      "No allowed payment methods returned from gpay",
      INTERNAL_ERROR_TYPE.GOOGLE_PAY.OnPurchase + "Error",
    );
  }

  const allowedPaymentMethods = tempPaymentDataRequest.allowedPaymentMethods.map((apm) => {
    apm.parameters.billingAddressRequired = true;
    apm.parameters.billingAddressParameters = {format: "FULL"};
    if (
      window.c_ActiveCurrency === "EUR" &&
      apm.type === "CARD" &&
      !apm.parameters.allowedCardNetworks.includes("AMEX")
    ) {
      apm.parameters.allowedCardNetworks.push("AMEX");
    }

    return apm;
  });

  const paymentDataRequest: google.payments.api.PaymentDataRequest = {
    apiVersion: 2,
    apiVersionMinor: 0,
    emailRequired: true,
    callbackIntents: ["SHIPPING_ADDRESS", "PAYMENT_AUTHORIZATION", "SHIPPING_OPTION"],
    shippingAddressRequired: true,
    shippingOptionRequired: true,
    shippingAddressParameters: {
      allowedCountryCodes: countries.map((country) => country.countryCode),
    },
    allowedPaymentMethods,

    merchantInfo: {
      merchantId,
      merchantName: displayName,
    },
    transactionInfo: {
      ...tempPaymentDataRequest.transactionInfo,
      totalPrice: cart.valuePayable.amount,
      totalPriceLabel: i18n.t("cart.total"),
      checkoutOption: "DEFAULT",
      countryCode: isoLocales[getShopId()].locale.split("-")[1],
      displayItems: positionsToDisplayItems(cart),
    },
  };

  return {
    gPayInstance,
    paymentDataRequest,
  };
};

/**
 * initialize process on payment data change with `callbackTrigger === "INITIALIZE"`
 * @param cart cart instance from save cart context
 * @param intermediatePaymentData changed payment data
 * @param paymentRequest paymentRequest state
 * @param checkoutRef reference that is to be set to `checkout` object
 */
export const initialize = async (
  cart: Cart,
  intermediatePaymentData: google.payments.api.IntermediatePaymentData,
  paymentRequest: google.payments.api.PaymentDataRequest,
  checkoutRef: React.MutableRefObject<Checkout | undefined>,
): Promise<ExtendedRequestUpdateType> => {
  if (cart.countryCode !== intermediatePaymentData.shippingAddress?.countryCode) {
    await cart.put({countryCode: intermediatePaymentData.shippingAddress?.countryCode});
  }
  checkoutRef.current = await retrieveCheckout(cart, Payments.GooglePay);

  return await processShippingOptions(
    checkoutRef.current,
    cart,
    paymentRequest.transactionInfo,
    intermediatePaymentData.shippingOptionData?.id,
  );
};

/**
 * Set new shipping address, and retrieve new shipping options, when address is set to new country code
 * @param cart cart instance from save cart context
 * @param checkout checkout from reference
 * @param intermediatePaymentData changed payment data
 * @param paymentRequest paymentRequest state
 */
export const handleShippingAddress = async (
  cart: Cart,
  checkout: Checkout,
  intermediatePaymentData: google.payments.api.IntermediatePaymentData,
  paymentRequest: google.payments.api.PaymentDataRequest,
): Promise<ExtendedRequestUpdateType> => {
  const noShippingRequestUpdate: ExtendedRequestUpdateType = {
    isPhoneRequired: false,
    requestUpdate: {
      error: {
        intent: "SHIPPING_ADDRESS",
        reason: "SHIPPING_ADDRESS_UNSERVICEABLE",
        message: i18n.t("checkout.noShippingToSelectedAddress"),
      },
    },
  };

  if (!intermediatePaymentData.shippingAddress) {
    return noShippingRequestUpdate;
  }

  const {countryCode, postalCode} = intermediatePaymentData.shippingAddress;
  if (isToNorthIreland(countryCode, postalCode)) {
    return noShippingRequestUpdate;
  }

  if (cart.countryCode !== countryCode) {
    await cart.put({countryCode});

    const {isPhoneRequired, requestUpdate} = await processShippingOptions(
      checkout,
      cart,
      paymentRequest.transactionInfo,
      intermediatePaymentData.shippingOptionData?.id,
    );

    if (!requestUpdate.newShippingOptionParameters?.shippingOptions.length) {
      requestUpdate.error = noShippingRequestUpdate.requestUpdate.error;
    }

    return {
      isPhoneRequired,
      requestUpdate,
    };
  }

  return {
    isPhoneRequired: !!paymentRequest.shippingAddressParameters?.phoneNumberRequired,
    requestUpdate: {
      newShippingOptionParameters: {
        shippingOptions: buildShippingOptions(globalShippingOptions, cart),
        defaultSelectedOptionId: intermediatePaymentData.shippingOptionData?.id,
      },
    },
  };
};

/**
 * Set new selected shipping option alongside updated cart total and display items
 * @param checkout
 * @param paymentRequest
 * @param shippingOptionData
 */
export const setNewShippingOption = async (
  checkout: Checkout,
  paymentRequest: google.payments.api.PaymentDataRequest,
  shippingOptionData: google.payments.api.SelectionOptionData,
): Promise<ExtendedRequestUpdateType> => {
  const logisticsTypeId = Number(shippingOptionData.id);
  await checkout.put({
    logisticsTypeId,
  });
  const isPhoneRequired = globalShippingOptions.find(
    (ship) => ship.logisticsTypeId === logisticsTypeId,
  )?.isPhoneRequired;

  return {
    isPhoneRequired,
    requestUpdate: {
      newShippingOptionParameters: {
        shippingOptions: paymentRequest.shippingOptionParameters?.shippingOptions ?? [],
        defaultSelectedOptionId: shippingOptionData.id,
      },
      newTransactionInfo: {
        ...paymentRequest.transactionInfo,
        displayItems: positionsToDisplayItems(checkout.cartData),
        totalPrice: checkout.cartData.value.payable.amount,
      },
    },
  };
};

/**
 * Get new shipping options, pick new default shipping, calculate new cart value
 *
 * @param checkout needed for `.getAvailableShippingOptions()`
 * @param cart needed for item positions and total amount
 * @param oldTransactionInfo needed for object destructuring in return value
 * @param shippingOptionDataId possibly formerly set shipping option
 */
const processShippingOptions = async (
  checkout: Checkout,
  cart: Cart,
  oldTransactionInfo: google.payments.api.TransactionInfo,
  shippingOptionDataId: string | undefined,
): Promise<ExtendedRequestUpdateType> => {
  globalShippingOptions = await checkout.getAvailableShippingOptions();
  if (!globalShippingOptions.length) {
    return {
      requestUpdate: {
        error: {
          reason: "SHIPPING_ADDRESS_UNSERVICEABLE",
          intent: "SHIPPING_OPTION",
          message: i18n.t("checkout.noShippingToSelectedAddress"),
        },
        newShippingOptionParameters: {
          shippingOptions: [],
        },
        newTransactionInfo: {
          ...oldTransactionInfo,
          displayItems: positionsToDisplayItems(cart),
          totalPrice: cart.valuePayable.amount,
        },
      },
    };
  }

  const defaultSelectedOption = findShippingOption(
    globalShippingOptions,
    shippingOptionDataId ? Number(shippingOptionDataId) : null,
  );
  const defaultSelectedOptionId = defaultSelectedOption?.logisticsTypeId.toString();

  return {
    isPhoneRequired: !!defaultSelectedOption?.isPhoneRequired,
    requestUpdate: {
      newShippingOptionParameters: {
        shippingOptions: buildShippingOptions(globalShippingOptions, cart),
        defaultSelectedOptionId,
      },
      newTransactionInfo: {
        ...oldTransactionInfo,
        displayItems: positionsToDisplayItems(cart),
        totalPrice: cart.valuePayable.amount,
      },
    },
  };
};

/**
 * Add some metadata to error for handling in Sentry
 *
 * @param err error from gpay
 */
export const prepareError = (err: google.payments.api.PaymentsError | Error): Error => {
  const {statusCode, statusMessage} = err as google.payments.api.PaymentsError;
  let gPayError: Error | undefined;
  if (statusCode || statusMessage) {
    gPayError = new Error(statusMessage || statusCode);
    gPayError.name = "Internal" + gPayError.name;
    (gPayError as Error & {gPayStatusCode: string}).gPayStatusCode = statusCode;
  }
  const errorToSend = gPayError || (err as Error);
  errorToSend.name = "GooglePay" + errorToSend.name;

  return errorToSend;
};

/**
 * Shipping options adapter method for `.map()`
 *
 * @param shippingOptions our shipping options to adapt to gpay shipping options
 * @param cart base for shipping price calculation
 */

const buildShippingOptions = (
  shippingOptions: IGetCheckoutShippingOptionsData[],
  cart: Cart,
): google.payments.api.SelectionOption[] => {
  return shippingOptions.map((shippingOption) => {
    let formattedAmount: string;
    if (shippingOption.amount.amount === "0.00") {
      formattedAmount = i18n.t("general.free");
    } else if (cart.isNet) {
      formattedAmount = formatCurrency({
        value: Number(shippingOption.amount.amount) / 1.19,
        currencyCode: cart.currencyCode,
      });
    } else {
      formattedAmount = shippingOption.amount.formatted;
    }

    return {
      id: shippingOption.logisticsTypeId.toString(),
      label: `${shippingOption.name} (${formattedAmount})`,
      description: shippingOption.description ?? undefined,
    };
  });
};

export const googleToInternalAddress = (
  gPayAddress: google.payments.api.Address,
  email: string,
  checkoutAddress: IAddressResponseData | null,
): IAddressData => {
  const {countryCode, postalCode, phoneNumber, locality, administrativeArea, name, address1} = gPayAddress;
  const recipientNameArray = name?.split(" ") ?? ["", ""];
  const lastName = recipientNameArray.pop();
  const firstName = recipientNameArray.join(" ");
  const [street, streetNumber] = splitStreetAndNumber(address1 ?? "", countryCode);

  return {
    company: checkoutAddress?.company || null,
    dateOfBirth: checkoutAddress?.dateOfBirth || null,
    department: checkoutAddress?.department || null,
    title: checkoutAddress?.title || null,
    gender: checkoutAddress?.gender || null,
    firstName,
    lastName: lastName || "",
    street,
    number: streetNumber,
    zip: postalCode,
    city: locality,
    state: administrativeArea || null,
    countryCode,
    email,
    phone: phoneNumber ?? null,
  };
};
